import React, { useState } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { Accordion, Button, Dropdown, Form, Table } from 'react-bootstrap'
import SortableWork from './SortableWork'
import StageMaterialsModal from './StageMaterialsModal'
import StageAsrModal from './StageAsrModal'
import StageTasksModal from './StageTasksModal'

const SortableWorkStage = ({
  workStage,
  index,
  project,
  onToggleCompletion,
  onEdit,
  onDelete,
  onReorderWorks,
  currentUser,
  onCollapse,
  selected,
  onToggleSelected,
}) => {
  const [showStageMaterials, setShowStageMaterials] = useState(false)
  const [showStageAsr, setShowStageAsr] = useState(false)
  const [showStageTasks, setShowStageTasks] = useState(false)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const completedClass = workStage.completed ? 'bg-success bg-opacity-10' : ''
  const position = index + 1

  const displayPrice = (price, priceForForeman) => {
    const isAdmin = currentUser?.roles?.includes('admin')
    if (isAdmin && priceForForeman !== price) {
      return (
        <>
          {price.toLocaleString('ru-RU')} ₽ <span className="small">/ {priceForForeman.toLocaleString('ru-RU')} ₽</span>
        </>
      )
    }
    return `${(isAdmin ? price : priceForForeman).toLocaleString('ru-RU')} ₽`
  }

  const handleWorkDragEnd = (event) => {
    const { active, over } = event

    if (!over || active.id === over.id) {
      return
    }

    if (onReorderWorks) {
      onReorderWorks(workStage.id, active.id, over.id)
    }
  }

  return (
    <div
      className={`sortable-item mb-3 ${completedClass}`}
      data-id={workStage.id}
    >
      <Accordion.Item eventKey={workStage.id.toString()}>
        <div className="d-flex align-items-center">
          <div
            className="stage-select px-2 py-3"
            onClick={(e) => e.stopPropagation()}
          >
            <Form.Check.Input
              type="checkbox"
              checked={!!selected}
              onChange={() => onToggleSelected(workStage.id)}
              title="Выбрать этап для формирования КП"
              aria-label={`Выбрать этап ${workStage.work_stage_name}`}
              style={{ cursor: 'pointer', width: '18px', height: '18px' }}
            />
          </div>
          <Accordion.Header className="flex-grow-1">
            <div className="d-flex flex-column flex-md-row w-100 justify-content-between align-items-md-center gap-2">
              <span className="fw-semibold">
                {workStage.completed && <span className="text-success me-2">✓</span>}
                {position}. {workStage.work_stage_name}
              </span>
<span className="fw-semibold">
                {displayPrice(workStage.total_price, workStage.total_price_for_foreman)}
              </span>
            </div>
          </Accordion.Header>
          <div className="px-2 d-flex flex-column flex-md-row gap-2" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => onEdit(workStage)}
              className="order-1 order-md-1"
            >
              Редактировать
            </Button>
            <Dropdown className="order-2 order-md-2">
              <Dropdown.Toggle variant="outline-secondary" size="sm">
                Действия
              </Dropdown.Toggle>
              <Dropdown.Menu align="end">
                <Dropdown.Item onClick={() => onToggleCompletion(workStage.id)}>
                  {workStage.completed ? '✓ Отметить как незавершенный' : 'Завершить этап'}
                </Dropdown.Item>
                {currentUser?.roles?.includes('admin') && (
                  <>
                    <Dropdown.Divider />
                    <Dropdown.Item
                      href={`/projects/${project.id}/work_stages/${workStage.id}/commercial_offer.pdf`}
                      target="_blank"
                    >
                      Коммерческое предложение
                    </Dropdown.Item>
                    <Dropdown.Item
                      href={`/projects/${project.id}/work_stages/${workStage.id}/commercial_offer.pdf?for_foreman=true`}
                      target="_blank"
                    >
                      Смета - М
                    </Dropdown.Item>
                  </>
                )}
                <Dropdown.Divider />
                <Dropdown.Item onClick={() => setShowStageTasks(true)}>
                  Задачи этапа
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setShowStageMaterials(true)}>
                  Материалы этапа
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setShowStageAsr(true)}>
                  Акты скрытых работ
                </Dropdown.Item>
                {currentUser?.roles?.includes('admin') && (
                  <Dropdown.Item
                    href={`/projects/${project.id}/work_stages/${workStage.id}/stage_completed_works_act.pdf`}
                    target="_blank"
                    disabled={!workStage.has_completed_works}
                  >
                    Акт выполненных работ
                    {!workStage.has_completed_works && ' (нет выполненных работ)'}
                  </Dropdown.Item>
                )}
                {currentUser?.roles?.includes('admin') && (
                  <>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={() => onDelete(workStage.id)} className="text-danger">
                      Удалить этап
                    </Dropdown.Item>
                  </>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
        <Accordion.Body>
          {workStage.works && workStage.works.length > 0 ? (
            <div className="table-responsive mb-3">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleWorkDragEnd}
              >
                <Table size="sm" className="align-middle mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}></th>
                      <th>Работа</th>
                      <th className="text-end">Объем</th>
                      <th className="text-end">Сумма, ₽</th>
                    </tr>
                  </thead>
                  <SortableContext
                    items={workStage.works.map((w) => w.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <tbody>
                      {workStage.works.map((work, idx) => (
                        <SortableWork
                          key={work.id}
                          work={work}
                          index={idx}
                          currentUser={currentUser}
                          displayPrice={displayPrice}
                        />
                      ))}
                    </tbody>
                  </SortableContext>
                </Table>
              </DndContext>
            </div>
          ) : (
            <p className="text-muted mb-3">Работы для этого этапа ещё не выбраны.</p>
          )}

          {workStage.works && workStage.works.length > 0 && (
            <div className="d-flex justify-content-center">
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={onCollapse}
              >
                Свернуть
              </Button>
            </div>
          )}
        </Accordion.Body>
      </Accordion.Item>

      <StageMaterialsModal
        show={showStageMaterials}
        onHide={() => setShowStageMaterials(false)}
        workStage={workStage}
        projectId={project.id}
      />

      <StageAsrModal
        show={showStageAsr}
        onHide={() => setShowStageAsr(false)}
        workStage={workStage}
        projectId={project.id}
      />

      <StageTasksModal
        show={showStageTasks}
        onHide={() => setShowStageTasks(false)}
        workStage={workStage}
        projectId={project.id}
      />
    </div>
  )
}

export default SortableWorkStage
